import { Component, Inject, OnInit, Optional } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";

import moment from "moment";
import { SharedModule } from "@coreui/angular";
import {
  visibility,
  ActionType,
  requestedBy,
  griveanceCategory,
  ActionDate,
  ArtefactFilterOptions,
  UserType,
  OrganizationType,
} from "src/models/manage-artifact-filter.model";
import { ArtefactType } from "src/models/models.model";
import { crossBlackSVG } from "src/constants/svg/cross-blackk";
import { InputLabelComponent } from "src/components/input-label/input-label.component";
import { DropdownInputComponent } from "../dropdown-input/dropdown-input.component";
import { SafeHtmlPipee } from "src/pipes/safe-html1.pipe";
import { MasterDataApiService } from "../master-data/master-data-api.service";
import { Sector } from "src/models/sector.model";
import { ApiService } from "src/services/api.service";
import { DateRangeInputComponent } from "src/components/date-range-input/date-range-input.component";

@Component({
  selector: "app-manage-artefact-filter-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    SharedModule,
    InputLabelComponent,
    DropdownInputComponent,
    DateRangeInputComponent,
    SafeHtmlPipee,
  ],
  templateUrl: "./manage-artefact-filter-dialog.component.html",
  styleUrls: ["./manage-artefact-filter-dialog.component.scss"],
})
export class ManageArtefactFilterDialogDialogComponent implements OnInit {
  filterForm!: FormGroup;
  customDateRange!: FormGroup;
  appliedFilterCount = 0;
  readonly crossBlackSVG = crossBlackSVG;
  readonly ActionDate = ActionDate;
  readonly UserType = UserType;
  appliedFilters: ArtefactFilterOptions | undefined;

  readonly actionDateLabels = {
    [ActionDate.weekly]: "Last Week",
    [ActionDate.monthly]: "Last Month",
    [ActionDate.custom]: "Custom",
  };

  readonly userTypeList = [
    { label: "Explorer", value: UserType.explorer },
    { label: "Contributor", value: UserType.contributor },
    { label: "Org Admin", value: UserType.orgAdmin },
  ];

  readonly visibility = [
    {
      label: "Open",
      value: visibility.open,
    },
    {
      label: "Restricted",
      value: visibility.restricted,
    },
  ];

  readonly requestedBy = [
    {
      label: "All",
      value: ActionType.all,
    },
    {
      label: "My Organisation",
      value: requestedBy.myOrganisation,
    },
    {
      label: "Outside of My Organisation",
      value: requestedBy.outsideOrganisation,
    },
  ];

  readonly artifactType = [
    {
      label: "Dataset",
      value: ArtefactType.dataset,
    },
    {
      label: "Model",
      value: ArtefactType.model,
    },
    {
      label: "Usecase",
      value: ArtefactType.usecase,
    },
    {
      label: "Toolkit",
      value: ArtefactType.toolkit,
    },
  ];

  readonly organizationType = [
    {
      label: "Public",
      value: OrganizationType.PUBLIC,
    },
    {
      label: "Private",
      value: OrganizationType.PRIVATE,
    },
    {
      label: "Research Organization",
      value: OrganizationType.RESEARCH_ORGANISATION,
    },
    {
      label: "Startup/MSME",
      value: OrganizationType.STARTUP_MSME,
    },
    {
      label: "University",
      value: OrganizationType.UNIVERSITY,
    },
  ];

  readonly category = [
    {
      value: griveanceCategory.accessibilityAndUsability,
      label: "Accessibility & Usability",
    },
    {
      value: griveanceCategory.artefactUploadAndDownloadIssues,
      label: "Artefact Upload and download issues",
    },
    {
      value: griveanceCategory.organizationAndTeamManagement,
      label: "Organization & Team Management",
    },
    {
      value: griveanceCategory.profileAndAccountIssues,
      label: "Profile & Account Issues",
    },
    {
      value: griveanceCategory.reportAArtefact,
      label: "Report a Artefact",
    },
    {
      value: griveanceCategory.technicalIssues,
      label: "Technical Issues",
    },
    {
      value: griveanceCategory.others,
      label: "Others",
    },
  ];

  sector: any;
  sourceOrganization: any;

  constructor(
    private masterapiService: MasterDataApiService,
    private apiService: ApiService,
    @Optional()
    private dialogRef: MatDialogRef<ManageArtefactFilterDialogDialogComponent> | null,
    @Inject(MAT_DIALOG_DATA)
    public dialogData?: { filters: ArtefactFilterOptions; config: any }
  ) {
    this.appliedFilters = dialogData?.filters;
    if (dialogRef) dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.dialogRef?.addPanelClass("manage-artefact-filter-dialog");
    this.initFilterForm();
    this.getCodeMastersList();
    this.getOrgList();
  }

  $formControl(control: AbstractControl) {
    return <FormControl>control;
  }

  getCodeMastersList() {
    const params: any[] = [
      { key: "page", value: "1" },
      { key: "size", value: "100" },
    ];

    this.masterapiService
      .getCodeMastersByCodeType("sectors", params)
      .subscribe((res) => {
        const sectorsOptions = {
          options: res?.data.list.map(({ name, id }: any) => ({
            label: name,
            value: id,
          })),
        };

        this.sector = sectorsOptions?.options;
      });
  }

  getOrgList() {
    this.apiService.getAllOrgList().subscribe((res) => {
      const orgOptions = {
        options: res?.data.map(({ name, id }: any) => ({
          label: name,
          value: id,
        })),
      };

      this.sourceOrganization = orgOptions?.options;
    });
  }

  private initFilterForm() {
    this.filterForm = new FormGroup({
      dateFilterType: new FormControl(null),
      visibility: new FormControl(null),
      requestedBy: new FormControl(null),
      category: new FormControl(null),
      artifactType: new FormControl(null),
      sectors: new FormControl(null),
      organizationType: new FormControl(null),
      sector: new FormControl(null),
      userType: new FormControl(null),
      sourceOrganization: new FormControl(null),
    });

    this.customDateRange = new FormGroup({
      fromDate: new FormControl("", [Validators.required]),
      toDate: new FormControl("", [Validators.required]),
    });

    this.prefillFilterValues();
  }

  private prefillFilterValues() {
    const {
      actionDate,
      dateFilterType,
      visibility = [],
      category = [],
      artifactType = [],
      requestedBy = [],
      sector = [],
      organizationType = [],
      userType = [],
      sourceOrganization = [],
    } = this.appliedFilters || {};

    this.filterForm.patchValue({
      dateFilterType: dateFilterType?.value,
      visibility: visibility?.map(({ value }) => value),
      requestedBy: requestedBy?.map(({ value }) => value),
      category: category?.map(({ value }) => value),
      artifactType: artifactType?.map(({ value }) => value),
      sector: sector?.map(({ value }) => value),
      organizationType: organizationType?.map(({ value }) => value),
      userType: userType?.map(({ value }) => value),
      sourceOrganization: sourceOrganization?.map(({ value }) => value),
    });

    if (actionDate) {
      const { fromDate, toDate } = actionDate;

      this.customDateRange.setValue({
        fromDate: moment(fromDate).format("YYYY-MM-DD"),
        toDate: moment(toDate).format("YYYY-MM-DD"),
      });
    }
  }

  setActionDate(value: ActionDate) {
    this.filterForm.get("dateFilterType")!.setValue(value);

    if (value !== ActionDate.custom) {
      this.customDateRange.reset();
    }
  }
  setUserType(value: UserType) {
    this.filterForm.get("userType")!.setValue([value]);
  }

  onClearFilters() {
    this.filterForm.reset();
    this.customDateRange.reset();
    this.onApplyFilters();
  }

  onApplyFilters() {
    const {
      visibility,
      requestedBy,
      dateFilterType,
      category,
      artifactType,
      organizationType,
      sector,
      userType,
      sourceOrganization,
    } = this.filterForm.value;

    if (dateFilterType === ActionDate.custom && this.customDateRange.invalid) {
      return;
    }

    const selectedValues: Partial<ArtefactFilterOptions> = {};

    if (dateFilterType) {
      selectedValues.dateFilterType = {
        label: this.actionDateLabels[dateFilterType as ActionDate] || "",
        value: dateFilterType,
      };
      selectedValues.actionDate = this.getActionDate(dateFilterType);
    }

    if (sourceOrganization?.length) {
      selectedValues.sourceOrganization = sourceOrganization.map(
        (item: string) => ({
          label: item,
          value: item,
        })
      );
    }
    if (userType?.length) {
      selectedValues.userType = userType.map((item: string) => ({
        label: item,
        value: item,
      }));
    }

    if (visibility?.length) {
      selectedValues.visibility = visibility.map((item: visibility) => ({
        label: this.visibility.find(({ value }) => item === value)!.label,
        value: item,
      }));
    }

    if (sector?.length) {
      selectedValues.sector = sector.map((item: string) => ({
        label: this.sector.find(
          ({ value }: { value: string }) => item === value
        )!.label,
        value: item,
      }));
    }

    if (requestedBy?.length) {
      selectedValues.requestedBy = requestedBy.map((item: requestedBy) => ({
        label: this.requestedBy.find(({ value }) => item === value)!.label,
        value: item,
      }));
    }

    if (category?.length) {
      selectedValues.category = category.map((item: griveanceCategory) => ({
        label: this.category.find(({ value }) => item === value)!.label,
        value: item,
      }));
    }

    if (artifactType?.length) {
      selectedValues.artifactType = artifactType.map((item: ArtefactType) => ({
        label: this.artifactType.find(({ value }) => item === value)!.label,
        value: item,
      }));
    }

    if (organizationType?.length) {
      selectedValues.organizationType = organizationType.map(
        (item: string) => ({
          label: item,
          value: item,
        })
      );
    }

    this.dialogRef?.close(selectedValues);
  }

  // onApplyFilters() {
  //   const {
  //     visibility,
  //     requestedBy,
  //     dateFilterType,
  //     category,
  //     artifactType,
  //     organizationType,
  //   } = this.filterForm.value;

  //   if (dateFilterType === ActionDate.custom && this.customDateRange.invalid) {
  //     return;
  //   }
  //   const selectedValues: ArtefactFilterOptions = {
  //     dateFilterType: {
  //       label: this.actionDateLabels[dateFilterType as ActionDate] || "",
  //       value: dateFilterType || "",
  //     },
  //     actionDate: this.getActionDate(dateFilterType),
  //     visibility:
  //       visibility?.map((item: visibility) => ({
  //         label: this.visibility.find(({ value }) => item === value)!.label,
  //         value: item,
  //       })) || [],

  //     requestedBy:
  //       requestedBy?.map((item: requestedBy) => ({
  //         label: this.requestedBy.find(({ value }) => item === value)!.label,
  //         value: item,
  //       })) || [],

  //     category:
  //       category?.map((item: griveanceCategory) => ({
  //         label: this.category.find(({ value }) => item === value)!.label,
  //         value: item,
  //       })) || [],

  //     artifactType:
  //       artifactType?.map((item: ArtefactType) => ({
  //         label: this.artifactType.find(({ value }) => item === value)!.label,
  //         value: item,
  //       })) || [],

  //     organizationType:
  //       organizationType?.map((item: string) => ({
  //         label: item,
  //         value: item,
  //       })) || [],
  //   };

  //   this.dialogRef?.close(selectedValues);
  // }

  getActionDate(dateFilterType: ActionDate | undefined): any {
    if (!dateFilterType) {
      return undefined; // Or a default value if needed
    }

    // if (dateFilterType !== ActionDate.custom) {
    //   return this.handleActionDate(dateFilterType);
    // }

    return this.customDateRange.value;
  }

  // handleActionDate(actionDate: string) {
  //   const toDate = new Date();
  //   let fromDate = new Date();

  //   if (actionDate === ActionDate.last7Days) {
  //     fromDate.setDate(fromDate.getDate() - 7);
  //   } else if (actionDate === ActionDate.last15Days) {
  //     fromDate.setDate(fromDate.getDate() - 15);
  //   }

  //   return { toDate, fromDate };
  // }
}
